import {ExternalCost} from '../../types'
import {useState} from 'react'
import {
    StyledExternalCostCard,
    StyledExternalCostCardBody,
    StyledExternalCostCardEdit,
    StyledExternalCostCardHeader,
    StyledDeleteExternalCostContent,
    StyledDeleteExternalCostFooter
} from './style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Button} from '@/components/ui/button/Button'
import {useTranslation} from '@/translations/i18n'
import {Controller, useForm, useWatch} from 'react-hook-form'
import {useStoreExternalCost} from '../../queries/useStoreExternalCost'
import {useUpdateExternalCost} from '../../queries/useUpdateExternalCost'
import {capitalize, errorHandler, raise} from '@/utilities/helpers'
import {ExternalCostFormSchema} from '../../formSchemas'
import {zodResolver} from '@hookform/resolvers/zod'
import {InputText} from '@/components/commons/input-text/InputText'
import {InputPrice} from '@/components/commons/input-price/InputPrice'
import {CurrencyEuroIcon, Pencil01Icon, Trash01Icon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {ExternalCostTypeField} from '../external-cost-type-field/ExternalCostTypeField'
import {HttpStoreExternalCostOptions} from '../../http/budget.http'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {yearsOptions} from '../../utils'
import dayjs from 'dayjs'
import {Switch} from '@/components/commons/switch/Switch'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Text} from '@/components/ui/text/Text'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {formatLocaleDate} from '@/dayjs'
import {
    Dialog,
    DialogBody,
    DialogDisclosure,
    DialogDismiss,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {useDeleteExternalCost} from '../../queries/useDeleteExternalCost'
import {Trans} from 'react-i18next'

const formatCurrency = (value: number) => {
    return Intl.NumberFormat('it-IT', {style: 'currency', currency: 'EUR'}).format(value)
}

const formatDate = (year: number, month: number) => {
    return capitalize(formatLocaleDate(`${year}-${month}`, 'MMM YYYY'))
}

export const ExternalCostCard = ({
    className,
    externalCost,
    setIsCreateMode,
    selectedYear
}: {
    className?: string
    externalCost?: ExternalCost
    setIsCreateMode: (isCreateMode: boolean) => void
    selectedYear: number
}) => {
    const {
        t,
        i18n: {language}
    } = useTranslation()
    const {palette} = useTheme()
    const [isEditMode, setIsEditMode] = useState(!externalCost)
    const {
        reset,
        formState: {errors},
        handleSubmit,
        register,
        control,
        setValue
    } = useForm<ExternalCostFormSchema>({
        resolver: zodResolver(ExternalCostFormSchema),
        defaultValues: externalCost
            ? {
                  description: externalCost.description,
                  amount: externalCost.amount.toString(),
                  type: externalCost.type,
                  period: externalCost.type == 'yearly' ? externalCost.year.toString() : externalCost.month.toString(),
                  until:
                      externalCost.type == 'yearly'
                          ? externalCost.untilYear?.toString()
                          : `${externalCost.untilYear}-${externalCost.untilMonth}`,
                  isRecurring: !!externalCost.untilYear
              }
            : undefined
    })

    const storeExternalCost = useStoreExternalCost({
        onError: errorHandler,
        onSuccess: () => {
            setIsEditMode(false)
            setIsCreateMode?.(false)
        }
    })
    const updateExternalCost = useUpdateExternalCost({
        onError: errorHandler,
        onSuccess: () => {
            setIsEditMode(false)
            setIsCreateMode?.(false)
        }
    })

    const deleteExternalCost = useDeleteExternalCost({
        onError: errorHandler
    })

    const onSubmit = handleSubmit(data => {
        const [untilYear, untilMonth] = data.until?.split('-') ?? []
        const payload = {
            description: data.description,
            amount: Number(data.amount),
            type: data.type,
            year: data.type == 'yearly' ? Number(data.period) : selectedYear,
            month: data.type == 'monthly' ? Number(data.period) : 12,
            untilYear: !data.isRecurring ? undefined : Number(untilYear),
            untilMonth: !data.isRecurring ? undefined : data.type == 'monthly' ? Number(untilMonth) : 12
        } satisfies HttpStoreExternalCostOptions['payload']

        if (externalCost) {
            updateExternalCost.mutate({
                urlParams: {
                    id: externalCost.id
                },
                payload
            })
        } else {
            storeExternalCost.mutate(payload)
        }
    })

    const [typeWatch, isRecurringWatch, periodWatch] = useWatch({
        control,
        name: ['type', 'isRecurring', 'period']
    })

    if (isEditMode) {
        const isPending = storeExternalCost.isPending || updateExternalCost.isPending

        return (
            <StyledExternalCostCardEdit className={className}>
                <InputText
                    label={t('budget:external_cost_description')}
                    type="text"
                    placeholder={t('budget:external_cost_description_placeholder')}
                    {...register('description')}
                    errorMessage={errors.description?.message}
                />
                <Controller
                    control={control}
                    name="amount"
                    render={({field, fieldState: {error}}) => (
                        <InputPrice
                            getInputRef={field.ref}
                            label={t('budget:external_cost_amount_label')}
                            placeholder={'0,00'}
                            name="amount"
                            value={field.value}
                            onValueChange={values => {
                                field.onChange(values.value)
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            fixedDecimalScale
                            allowNegative={false}
                            typeIcon={<CurrencyEuroIcon size={16} color={palette.neutral[500]} />}
                            errorMessage={error?.message}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="type"
                    render={({field, fieldState: {error}}) => (
                        <ExternalCostTypeField
                            name={field.name}
                            value={field.value}
                            label={t('budget:external_cost_type_label')}
                            onChange={value => {
                                field.onChange(value)
                                setValue('period', '')
                                setValue('until', '')
                            }}
                            error={error}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="period"
                    render={({field, fieldState: {error}}) => (
                        <InputSelect
                            disabled={!typeWatch}
                            label={t('budget:external_cost_period_label')}
                            optionsEntries={
                                typeWatch == 'yearly'
                                    ? yearsOptions.map(year => [year, year])
                                    : Array.from({length: 12}, (_, i) => {
                                          const monthDate = dayjs().month(i).year(selectedYear).locale(language)
                                          return [`${i + 1}`, capitalize(monthDate.format('MMMM YYYY'))]
                                      })
                            }
                            value={field.value}
                            onChange={value => {
                                field.onChange(value)
                            }}
                            errorMessage={error?.message}
                        />
                    )}
                />
                <Flexbox direction="column" align="flex-end" gap={1.5} fullWidth>
                    <Flexbox align="center" gap={4} fullWidth>
                        <Controller
                            control={control}
                            name="isRecurring"
                            render={({field, fieldState: {error}}) => (
                                <Flexbox direction="column" gap={1.5}>
                                    <Switch
                                        size="sm"
                                        label={t('budget:external_cost_is_recurring_label')}
                                        checked={field.value}
                                        onChange={field.onChange}
                                    />
                                    {!!error && <InputHelpText error={error.message} />}
                                </Flexbox>
                            )}
                        />
                        {isRecurringWatch && (
                            <Flexbox gap={2} align="center" fullWidth>
                                <Text size="textSm" weight="500" style={{flexShrink: 0}}>
                                    {t('budget:until_label')}
                                </Text>
                                <Controller
                                    control={control}
                                    name="until"
                                    render={({field}) => (
                                        <InputSelect
                                            sameWidth={false}
                                            disabled={!periodWatch}
                                            optionsEntries={
                                                typeWatch == 'yearly'
                                                    ? Array.from({length: 2}, (_, indexYear): [string, string] => {
                                                          const year = Number(periodWatch) + indexYear + 1
                                                          return [year.toString(), year.toString()]
                                                      })
                                                    : Array.from({length: 3}, (_, indexYear) => {
                                                          return Array.from(
                                                              {length: 12},
                                                              (_, indexMonth): [string, string] => {
                                                                  const year = selectedYear + indexYear
                                                                  const monthDate = dayjs()
                                                                      .month(indexMonth)
                                                                      .year(year)
                                                                      .locale(language)
                                                                  return [
                                                                      `${year}-${indexMonth + 1}`,
                                                                      capitalize(monthDate.format('MMMM YYYY'))
                                                                  ]
                                                              }
                                                          ).filter(([month]) =>
                                                              dayjs(month, 'YYYY-M').isAfter(
                                                                  dayjs(`${selectedYear}-${periodWatch}`, 'YYYY-M')
                                                              )
                                                          )
                                                      }).flat()
                                            }
                                            value={field.value}
                                            onChange={value => {
                                                field.onChange(value)
                                            }}
                                        />
                                    )}
                                />
                            </Flexbox>
                        )}
                    </Flexbox>
                    {!!errors.until && <InputHelpText error={errors.until.message} />}
                </Flexbox>
                <Flexbox gap={1}>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                            reset()
                            setIsEditMode(false)
                            setIsCreateMode?.(false)
                        }}
                    >
                        {t('commons:cancel')}
                    </Button>
                    <Button size="sm" variant="primaryNeutral" disabled={isPending} onClick={onSubmit}>
                        {t('commons:confirm')}
                        {isPending && <Spinner />}
                    </Button>
                </Flexbox>
            </StyledExternalCostCardEdit>
        )
    }

    return (
        <StyledExternalCostCard className={className}>
            <StyledExternalCostCardHeader>
                <Text size="textLg" weight="500">
                    {externalCost?.description}
                </Text>
                <Flexbox gap={2}>
                    <DialogProvider>
                        <DialogDisclosure render={<Button variant="ghostDanger" size="sm" />}>
                            <Trash01Icon />
                            {t('commons:delete')}
                        </DialogDisclosure>
                        <Dialog>
                            <DialogBody gap={2}>
                                <Text render="h1" size="textXl" weight={'500'}>
                                    {t('budget:delete_external_cost_title')}
                                </Text>
                                <StyledDeleteExternalCostContent>
                                    <Trans
                                        i18nKey="budget:delete_external_cost_description"
                                        values={{
                                            description: externalCost?.description,
                                            type: (function () {
                                                if (externalCost?.type == 'yearly') {
                                                    return externalCost?.untilYear
                                                        ? t('budget:yearly_recurring')
                                                        : t('budget:yearly')
                                                } else {
                                                    return externalCost?.untilYear
                                                        ? t('budget:monthly_recurring')
                                                        : t('budget:monthly')
                                                }
                                            })(),
                                            period: (function () {
                                                if (externalCost?.type == 'yearly') {
                                                    return externalCost?.untilYear
                                                        ? `${externalCost.year} - ${externalCost.untilYear}`
                                                        : externalCost.year
                                                } else {
                                                    return externalCost?.untilMonth && externalCost?.untilYear
                                                        ? `${formatDate(externalCost.year, externalCost.month)} - ${formatDate(externalCost.untilYear, externalCost.untilMonth)}`
                                                        : formatDate(externalCost?.year ?? 0, externalCost?.month ?? 0)
                                                }
                                            })(),
                                            amount: formatCurrency(externalCost?.amount ?? 0)
                                        }}
                                        components={{br: <br />, b: <b />, ul: <ul />, li: <li />}}
                                    />
                                </StyledDeleteExternalCostContent>
                            </DialogBody>
                            <StyledDeleteExternalCostFooter>
                                <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
                                <Button
                                    variant="primaryDanger"
                                    onClick={() =>
                                        deleteExternalCost.mutate({
                                            urlParams: {id: externalCost?.id ?? raise('externalCost is nullish')}
                                        })
                                    }
                                    disabled={deleteExternalCost.isPending}
                                >
                                    {t('commons:delete')}
                                    {deleteExternalCost.isPending && <Spinner />}
                                </Button>
                            </StyledDeleteExternalCostFooter>
                        </Dialog>
                    </DialogProvider>
                    <Button variant="ghost" size="sm" onClick={() => setIsEditMode(true)}>
                        <Pencil01Icon />
                        {t('commons:edit')}
                    </Button>
                </Flexbox>
            </StyledExternalCostCardHeader>
            <StyledExternalCostCardBody>
                <Flexbox direction="column" gap={1.5}>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('budget:external_cost_amount_label')}
                    </Text>
                    <Text size="textSm">{formatCurrency(externalCost?.amount ?? 0)}</Text>
                </Flexbox>
                <Flexbox direction="column" gap={1.5}>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('budget:external_cost_type_label')}
                    </Text>
                    <Text size="textSm">{externalCost?.type == 'yearly' ? t('commons:year') : t('commons:month')}</Text>
                </Flexbox>
                <Flexbox direction="column" gap={1.5}>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('budget:external_cost_period_label')}
                    </Text>
                    <Text size="textSm">
                        {externalCost?.type == 'yearly'
                            ? externalCost.year
                            : formatDate(externalCost?.year ?? 0, externalCost?.month ?? 0)}
                    </Text>
                </Flexbox>
                <Flexbox direction="column" gap={1.5}>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('budget:external_cost_recurring_label')}
                    </Text>
                    <Text size="textSm">
                        {!externalCost?.untilYear
                            ? '-'
                            : externalCost.type == 'yearly'
                              ? t('budget:every_year_until', {value: externalCost.untilYear})
                              : t('budget:every_month_until', {
                                    value: formatDate(externalCost?.untilYear ?? 0, externalCost?.untilMonth ?? 0)
                                })}
                    </Text>
                </Flexbox>
            </StyledExternalCostCardBody>
        </StyledExternalCostCard>
    )
}
